import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from '@/lib/toast'
import i18n from '@/i18n'
import { api } from '@/lib/api'
import type { ContactShare, ShareResponse, IntroductionDraft } from '@/lib/types'

export function useContactShares(contactId: string | undefined) {
  return useQuery({
    queryKey: ['contactShares', contactId],
    queryFn: () => api<{ items: ContactShare[] }>(`/api/contacts/${contactId}/shares`),
    enabled: !!contactId,
  })
}

export function useCreateShare() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      contactId,
      includedFields,
      expiresInDays,
    }: {
      contactId: string
      includedFields?: string[]
      expiresInDays?: number
    }) =>
      api<ShareResponse>(`/api/contacts/${contactId}/share`, {
        method: 'POST',
        body: JSON.stringify({ includedFields, expiresInDays }),
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['contactShares', vars.contactId] })
      toast.success(i18n.t('sharing.linkCreated', 'Share link created'))
    },
    onError: () => {
      toast.error(i18n.t('sharing.error', 'Failed to create share link'))
    },
  })
}

export function useDeleteShare() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ contactId, shareId }: { contactId: string; shareId: string }) =>
      api<void>(`/api/contacts/${contactId}/share/${shareId}`, { method: 'DELETE' }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['contactShares', vars.contactId] })
      toast.success(i18n.t('sharing.linkRevoked', 'Share link revoked'))
    },
  })
}

export function useDraftIntroduction() {
  return useMutation({
    mutationFn: ({
      contactId1,
      contactId2,
      context,
    }: {
      contactId1: string
      contactId2: string
      context?: string
    }) =>
      api<IntroductionDraft>('/api/contacts/introduce', {
        method: 'POST',
        body: JSON.stringify({ contactId1, contactId2, context }),
      }),
    onError: (err: Error & { status?: number }) => {
      if (err.status === 403) {
        toast.error(i18n.t('sharing.premiumRequired', 'Premium subscription required'))
      } else {
        toast.error(i18n.t('sharing.introError', 'Failed to draft introduction'))
      }
    },
  })
}

export function downloadVCard(contactId: string) {
  window.open(`/api/contacts/${contactId}/vcard`, '_blank')
}
